/**
 * Multiplayer Service - WebSocket-based multiplayer
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');

class MultiplayerService {
  constructor() {
    this.ws = null;
    this.roomId = null;
    this.role = null;
    this.callbacks = {
      onConnect: null,
      onMove: null,
      onPlayerJoined: null,
      onPlayerDisconnected: null,
      onGameOver: null,
      onDrawOffer: null,
      onChat: null,
      onError: null
    };
  }

  async createRoom(hostName, hostColor = 'white') {
    const response = await fetch(`${API_URL}/api/multiplayer/create-room`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ host_name: hostName, host_color: hostColor })
    });
    
    if (!response.ok) {
      throw new Error('Failed to create room');
    }
    
    return await response.json();
  }

  async getRoomInfo(roomId) {
    const response = await fetch(`${API_URL}/api/multiplayer/room/${roomId}`, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Room not found');
    }
    
    return await response.json();
  }

  async joinRoom(roomId, guestName) {
    const response = await fetch(`${API_URL}/api/multiplayer/room/${roomId}/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ guest_name: guestName })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to join room');
    }
    
    return await response.json();
  }

  connect(roomId, callbacks = {}) {
    this.roomId = roomId;
    this.callbacks = { ...this.callbacks, ...callbacks };
    
    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(`${WS_URL}/api/ws/game/${roomId}`);
      
      this.ws.onopen = () => {
        console.log('WebSocket connected');
      };
      
      this.ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        this.handleMessage(data, resolve);
      };
      
      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        if (this.callbacks.onError) {
          this.callbacks.onError(error);
        }
        reject(error);
      };
      
      this.ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        if (this.callbacks.onPlayerDisconnected) {
          this.callbacks.onPlayerDisconnected({ reason: event.reason });
        }
      };
    });
  }

  handleMessage(data, resolveConnect) {
    switch (data.type) {
      case 'connected':
        this.role = data.role;
        if (this.callbacks.onConnect) {
          this.callbacks.onConnect(data);
        }
        resolveConnect(data);
        break;
        
      case 'player_joined':
        if (this.callbacks.onPlayerJoined) {
          this.callbacks.onPlayerJoined(data);
        }
        break;
        
      case 'move':
        if (this.callbacks.onMove) {
          this.callbacks.onMove(data);
        }
        break;
        
      case 'game_over':
        if (this.callbacks.onGameOver) {
          this.callbacks.onGameOver(data);
        }
        break;
        
      case 'draw_offer':
        if (this.callbacks.onDrawOffer) {
          this.callbacks.onDrawOffer(data);
        }
        break;
        
      case 'chat':
        if (this.callbacks.onChat) {
          this.callbacks.onChat(data);
        }
        break;
        
      case 'player_disconnected':
        if (this.callbacks.onPlayerDisconnected) {
          this.callbacks.onPlayerDisconnected(data);
        }
        break;
    }
  }

  sendMove(move, fen, history, gameOver = false, winner = null) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'move',
        move,
        fen,
        history,
        gameOver,
        winner
      }));
    }
  }

  resign() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'resign' }));
    }
  }

  offerDraw() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'draw_offer' }));
    }
  }

  acceptDraw() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'draw_accept' }));
    }
  }

  sendChat(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'chat', message }));
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.roomId = null;
    this.role = null;
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Singleton instance
const multiplayerService = new MultiplayerService();
export default multiplayerService;
